import React, { KeyboardEvent, useContext, useState } from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { PrimaryButton, Title4, InfoInpage, SearchTextField, Asset } from '@hz-design-system/web-ui';

import { linkGeneratorHelper, generateLinkObject } from '@/utils/links/linkGeneratorHelper';
import getClientPageQueryObject from '@/client/utils/getClientPageQueryObject';
import pushToRouter from '@/client/utils/pushToRouter';
import trackEvent from '@/client/utils/analytics/trackEvent';
import removeSavedSearchFlag from '@/client/utils/searchRequestUtils';
import CategorySuggestionTooltip from '@/client/components/CategorySuggestionTooltip/CategorySuggestionTooltip';
import useIsMarktplaats from '@/client/hooks/useIsMarktplaats';

import experiments from '@/utils/labs/experiments';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import CATEGORIES_L1 from '@/constants/categories';

interface ZRPSearchHelperProps {
  searchRequestObject: TSearchRequestObject;
  hasErrors?: boolean;
  suggestions?: TSuggestedCategories;
}

const ZRPSearchHelper: React.FC<ZRPSearchHelperProps> = ({ searchRequestObject, hasErrors, suggestions }) => {
  const { t } = useI18nContext();
  const { labsConfig } = useContext(EnvironmentContext);
  const {
    isFetchResultsOnceEnabled,
    isFetchResultsOnceAllCatEnabled,
    isFetchResultsHIOnceEnabled,
    isCategorySuggestionTooltipEnabled,
    isAttributeSuggestionTooltipEnabled,
  } = experiments({
    labsConfig,
  });

  const isMarktplaats = useIsMarktplaats();

  const withAllAttributes =
    isFetchResultsOnceAllCatEnabled ||
    (isFetchResultsOnceEnabled && searchRequestObject.categories?.l1Category?.id === CATEGORIES_L1.DAMES_KLEREN) ||
    (isFetchResultsHIOnceEnabled &&
      isMarktplaats &&
      searchRequestObject.categories?.l1Category?.id === CATEGORIES_L1.HUIS_EN_INRICHTING);
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const { searchQuery } = searchRequestObject;

  const handleSubmit = () => {
    const keyword = searchValue || searchQuery;

    if (keyword && keyword.trim()) {
      const options = {
        query: keyword.trim(),
        categories: searchRequestObject.categories,
        searchRequestObject: removeSavedSearchFlag(searchRequestObject),
        withAllAttributes,
        removeAllFilters: false,
        removeAllAttributes: false,
      };
      const href = linkGeneratorHelper(options);
      const query = getClientPageQueryObject(generateLinkObject(options));

      trackEvent({
        eventAction: 'ZRPSearchClick',
        eventLabel: 'ZRPExperiment',
      });

      pushToRouter({ query, href });
    }
  };

  const handleEnterKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.code === 'Enter') {
      handleSubmit();
    }
  };

  const getDescriptionKey = () => {
    if (hasErrors) return '_error';
    if (!searchQuery) return '_no_query';
    return '';
  };

  return (
    <div className="zrp-search-helper">
      <Asset.Fluid name="buyers-no-results" />
      <div className="zrp-search-helper-text">
        <Title4>{t('messages.no_results_found_search_title')}</Title4>
        <Trans
          className="zrp-search-helper-desc"
          br={Trans.safeString('<br>')}
          query={searchQuery}
          i18nKey={`messages.no_results_found_search_description${getDescriptionKey()}`}
          tagName="div"
        />
      </div>
      <div className="zrp-search-helper-search-tips">
        {(isCategorySuggestionTooltipEnabled || isAttributeSuggestionTooltipEnabled) && (
          <div className="zrp-search-helper-suggestions">
            <CategorySuggestionTooltip
              suggestedCategories={suggestions?.categories || []}
              searchRequestObject={searchRequestObject}
              isSticky={false}
              showHighlight={false}
            />
          </div>
        )}
        <div className="zrp-search-helper-search">
          <SearchTextField
            placeholder={searchQuery}
            value={searchValue}
            onChange={handleSearch}
            onKeyPress={handleEnterKeyPress}
            ariaLabel={t('messages.no_results_search_input_aria_label')}
          />
          <PrimaryButton className="zrp-search-helper-search-btn" onClick={handleSubmit}>
            {t('messages.no_results_found_search_box_button')}
          </PrimaryButton>
        </div>
        <InfoInpage icon="info" description={t('messages.no_results_found_search_tips')} />
      </div>
    </div>
  );
};

export default ZRPSearchHelper;
